import React, { useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { TrelloLabel } from '../types/labels';

interface Board {
  id: string;
  name: string;
  url: string;
}

interface List {
  id: string;
  name: string;
}

interface BoardSelectorProps {
  boards: Board[];
  selectedBoard: string;
  onBoardChange: (boardId: string) => void;
  loading?: boolean;
  className?: string;
}

const BoardSelector: React.FC<BoardSelectorProps> = ({
  boards,
  selectedBoard,
  onBoardChange,
  loading = false,
  className = ''
}) => {
  const [isBoardOpen, setIsBoardOpen] = useState(false);

  const selectedBoardData = boards.find(board => board.id === selectedBoard);

  const handleBoardSelect = (boardId: string) => {
    onBoardChange(boardId);
    setIsBoardOpen(false);
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-12 bg-gray-200 rounded-lg"></div>
          <div className="h-12 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Board Selector */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Board
          </label>
          <button
            type="button"
            onClick={() => setIsBoardOpen(!isBoardOpen)}
            className="relative w-full bg-white border border-gray-300 rounded-lg px-3 py-3 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <span className="block truncate">
              {selectedBoardData ? selectedBoardData.name : 'Choose a board...'}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  isBoardOpen ? 'rotate-180' : ''
                }`}
              />
            </span>
          </button>

          {isBoardOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
              {boards.map((board) => (
                <button
                  key={board.id}
                  onClick={() => handleBoardSelect(board.id)}
                  className="w-full text-left px-4 py-2 hover:bg-indigo-50 flex items-center justify-between"
                >
                  <span className="truncate">{board.name}</span>
                  {selectedBoard === board.id && (
                    <Check className="h-5 w-5 text-indigo-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardSelector; 